<template>
  <content-dialog
    ref="dialog"
    v-model="dialogOpen"
    title="File"
    dark
    maximized
    card-class="bg-grey-10"
    transition-show="fade"
    :loading="loading"
    @hide="$emit('close')"
  >
    <template #card>
      <q-layout
        container
        view="hHr lpR fFr"
      >
        <q-header
          v-if="$refs.dialog"
          class="blurred-container dark"
        >
          <q-toolbar>
            <q-btn
              v-close-popup
              flat
              round
              icon="mdi-close"
              :disable="loading"
            />
            <page-toolbar-title
              class="text-body1 cursor-pointer"
              @click="drawerOpen = !drawerOpen"
            >
              <span v-if="preview">Preview File</span>
              <span v-else>{{ truncateString(file && file.file_name ? file.file_name : 'View File', 100) }}</span>

              <template v-if="!loading && !isAllowedEditing" #subtitle>
                Read-only
              </template>
            </page-toolbar-title>

            <q-btn
              v-if="!preview"
              dense
              round
              flat
              icon="mdi-download"
              :disable="!file"
              @click="goToHref(file.signed_url, true)"
            >
              <q-tooltip>Download File</q-tooltip>
            </q-btn>

            <transition name="growLeftTransition">
              <q-btn
                v-if="!drawerOpen"
                dense
                round
                flat
                icon="mdi-menu-open"
                class="q-ml-sm"
                @click="drawerOpen = !drawerOpen"
              >
                <q-tooltip>File Details</q-tooltip>
              </q-btn>
            </transition>
          </q-toolbar>
        </q-header>

        <q-drawer
          v-model="drawerOpen"
          side="right"
          class="text-black"
          :width="windowWidth < 375 ? windowWidth : 375"
          :breakpoint="breakpoint"
        >
          <DetailsPartial
            v-if="!!file"
            :file="file"
            :errors="errors"
            :saving="saving"
            :is-allowed-editing="isAllowedEditing"
            @save-file="saveFile"
            @close="drawerOpen = false"
          />
        </q-drawer>

        <q-page-container>
          <q-page
            class="flex flex-center column q-pa-none"
            :padding="!file || file.file_extension !== 'pdf'"
          >
            <q-inner-loading
              :showing="loading"
              dark
            >
              <loading-spinner />
            </q-inner-loading>
            <FilePartial
              v-if="!loading && file && file.signed_url"
              class="full-width"
              :class="{'flex-auto': file.file_extension === 'pdf'}"
              :file="file"
            />
            <div v-else-if="!loading">
              <no-results
                icon="mdi-file-cancel-outline"
                title="Could not load file"
              />
            </div>
          </q-page>
        </q-page-container>

        <q-footer
          v-if="$refs.dialog && file && (isAllowedDelete || canPhotoAnnotate)"
          class="blurred-container dark"
        >
          <q-toolbar>
            <q-space />
            <q-btn
              v-if="isAllowedDelete"
              flat
              icon-right="mdi-delete"
              label="Delete"
              color="red-3"
              :disable="!file"
              @click="deleteFile"
            />
            <q-btn
              v-if="canPhotoAnnotate"
              color="white"
              text-color="black"
              icon-right="mdi-pencil"
              label="Edit"
              class="q-ml-sm"
              @click="goTo('drawings/annotate', { file_id: file.id, back: true })"
            />
          </q-toolbar>
        </q-footer>
      </q-layout>
    </template>
  </content-dialog>
</template>

<script>
import fileMixin from '../mixins/fileMixin'

import FilePartial from './partials/File'
import DetailsPartial from './partials/Details'
import { truncateString } from 'src/helpers/globals'

export default {
  components: {
    DetailsPartial, FilePartial
  },
  mixins: [fileMixin],
  props: {
    id: {
      type: Number,
      default: null
    },
    canDelete: {
      type: Boolean,
      default: true
    },
    preview: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'saved', 'deleted'],
  data () {
    return {
      breakpoint: 1100,
      loading: false,
      dialogOpen: false,
      file: null,
      saving: false,
      errors: {},
      drawerOpen: false
    }
  },
  computed: {
    isAllowedDelete () {
      return !this.preview && this.file && this.file.can_delete && this.canDelete
    },
    isAllowedEditing () {
      return !this.preview && this.file && this.file.can_edit
    },
    canPhotoAnnotate () {
      return this.isAllowedEditing && this.can(this.permissions.canAccessDrawings) && this.file.type === 'IMAGE'
    }
  },
  watch: {
    'id' (val) {
      if (!val) {
        this.dialogOpen = false
        return
      }
      this.open()
    },
    'drawerOpen' (val) {
      this.$store.commit('setSetting', { key: 'fileDrawerOpen', value: val })
    }
  },
  methods: {
    truncateString,
    open () {
      this.loadFile()
    },
    async loadFile () {
      if (this.id === null) { return }
      this.loading = true
      this.dialogOpen = true
      const { status, payload } = await this.$get('v1/files', {
        id: this.id
      })
      this.loading = false
      if (status === 'success') {
        this.file = payload.file
      }
    },
    async saveFile () {
      this.saving = true
      this.errors = {}
      const { status, payload, errors } = await this.$post('v1/files', {
        id: this.id,
        title: this.file.title,
        description: this.file.description,
        category_id: this.file.category_id,
        visibility: this.file.visibility
      })
      this.saving = false
      if (status === 'success') {
        this.$q.notify({
          color: 'green',
          message: 'File details successfully updated'
        })
        this.$emit('saved', payload.file)
        this.$bus.emit('files:reload', true)
      }
      else {
        this.errors = errors
        this.$nextTick(() => { this.scrollToFirstError(this.$el) })
      }
    },
    deleteFile () {
      this.baseDeleteFile(
        this.file,
        () => {
          this.$bus.emit('files:reload', true)
          this.$emit('deleted', this.id)
          this.dialogOpen = false
        }
      )
    }
  },
  mounted () {
    // Must be done here - windowWidth isn't available in data properties at that point
    this.drawerOpen = this.windowWidth > this.breakpoint ? (this.$store.getters.getSetting('fileDrawerOpen') || false) : false
    this.loadFile()
  }
}
</script>
